import { ComponentProps, FocusEvent, ReactNode, useId, useState } from 'react';

import { Label as LabelUI } from '@/components/ui/label';
import { Textarea as TextareaUI } from '@/components/ui/textarea';

import { cn } from '@/lib/utils';

type TextareaProps = Omit<ComponentProps<'textarea'>, 'placeholder'> & {
  label?: ReactNode;
};

const Textarea = (props: TextareaProps) => {
  const {
    className,
    label,
    id,
    onFocus,
    onBlur,
    value,
    defaultValue,
    'aria-invalid': ariaInvalid,
    ...restProps
  } = props;

  const generatedId = useId();
  const textareaId = id ?? generatedId;

  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(() => {
    return Boolean(value ?? defaultValue);
  });

  const isFloating = isFocused || hasValue;

  const handleFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    setHasValue(Boolean(e.target.value));
    onBlur?.(e);
  };

  return (
    <div className="relative">
      <TextareaUI
        id={textareaId}
        value={value}
        defaultValue={defaultValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          'dark:bg-card border-transparent bg-transparent pt-4 placeholder:text-transparent',
          'focus-visible:border-transparent',
          'aria-invalid:border-transparent',
          className,
        )}
        aria-invalid={ariaInvalid}
        {...restProps}
      />
      {label && (
        <>
          <fieldset
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 top-[-5px] overflow-hidden rounded-md px-[9px] transition-colors',
              isFocused && !ariaInvalid && 'border-primary border-2',
              !isFocused && !ariaInvalid && 'border-input border-2',
              ariaInvalid && 'border-destructive border-2',
            )}
          >
            <legend
              className={cn(
                'invisible block h-[11px] w-auto overflow-hidden p-0 text-xs whitespace-nowrap',
                isFloating
                  ? 'max-w-full transition-[max-width] delay-50 duration-100 ease-out'
                  : 'max-w-[0.01px] transition-[max-width] duration-50 ease-out',
              )}
            >
              <span className="visible inline-block px-1 opacity-0">
                {label}
              </span>
            </legend>
          </fieldset>
          <LabelUI
            htmlFor={textareaId}
            className={cn(
              'text-muted-foreground pointer-events-none absolute left-[13px] gap-1 transition-all duration-200 ease-out',
              isFloating ? 'top-0 -translate-y-1/2 text-xs' : 'top-4 text-sm',
              isFocused && !ariaInvalid && 'text-primary',
              ariaInvalid && 'text-destructive',
            )}
          >
            {label}
          </LabelUI>
        </>
      )}
    </div>
  );
};

export { Textarea };
export type { TextareaProps };
